import { AttachmentIcon, DeleteIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Spinner,
  Text,
  VStack,
  Image,
  useToast,
} from "@chakra-ui/react";
import { Suspense, useCallback, useContext, useMemo, useRef } from "react";
import { SpotType } from "~/generated/graphql.types";
import { EditModeContext } from "../..";
import {
  useGetSpotQuery,
  useGetSpotAssetQuery,
  useUpdateSpotAssetMutation,
  useGetCourseAssetQuery,
  useUpdateCourseAssetMutation,
  useRemoveCourseAssetMutation,
  useRemoveSpotAssetMutation,
} from "./index.generated";
import { CourseAssetType, SpotAssetType } from "~/generated/graphql.types";

const Inner = ({ courseId }: { courseId: string }) => {
  const { spotIdForEdit, courseIdForEdit } = useContext(EditModeContext);
  const [getSpotRes] = useGetSpotQuery({
    variables: {
      id: spotIdForEdit!,
    },
    pause: !spotIdForEdit,
  });
  const spot = getSpotRes.data?.spot!;

  const FormForSpecifiedSpot = useCallback(() => {
    return <SpotImageForm spotId={spot.id} />;
  }, [spotIdForEdit]);

  return spot && spotIdForEdit !== undefined ? (
    spot.spotType !== SpotType.RoutingGuide ? (
      <FormForSpecifiedSpot />
    ) : (
      <Box w="full" h="full">
        <Text fontSize="xl" color="white" textAlign="center" fontWeight={600}>
          ルート誘導用スポットは編集できません
        </Text>
      </Box>
    )
  ) : courseId !== undefined ? (
    <Box w="full" h="full">
      <CourseImageForm courseId={courseId} />
    </Box>
  ) : (
    <Box w="full" h="full" m={16}>
      <Text fontSize="xl" color="white" textAlign="center" fontWeight={600}>
        スポットを選択してください
      </Text>
    </Box>
  );
};

const SpotImageForm = ({ spotId }: { spotId: string }) => {
  const toast = useToast();
  const { setSpotIdForEdit } = useContext(EditModeContext);

  const detail_ref = useRef<HTMLInputElement>(null);
  const thumb_ref = useRef<HTMLInputElement>(null);
  const balloon_ref = useRef<HTMLInputElement>(null);
  const noimage = "";

  const [getDetail] = useGetSpotAssetQuery({
    variables: {
      spotId: spotId,
      assetType: SpotAssetType.Detail,
    },
  });
  const [getThumb] = useGetSpotAssetQuery({
    variables: {
      spotId: spotId,
      assetType: SpotAssetType.Thumb,
    },
  });
  const [getBalloon] = useGetSpotAssetQuery({
    variables: {
      spotId: spotId,
      assetType: SpotAssetType.Balloon,
    },
  });
  const [updateRes, update] = useUpdateSpotAssetMutation();

  const [removeSpotAssetRes, remove] = useRemoveSpotAssetMutation();

  const detailImage = useMemo(() => {
    if (getDetail.data?.getSpotAsset) {
      if (getDetail.data.getSpotAsset.source.length === 0) {
        return noimage;
      }

      return createObjectURL(getDetail.data.getSpotAsset.source);
    }
    return noimage;
  }, [getDetail.data]);

  const thumbImage = useMemo(() => {
    if (getThumb.data?.getSpotAsset) {
      if (getThumb.data.getSpotAsset.source.length === 0) {
        return noimage;
      }
      return createObjectURL(getThumb.data.getSpotAsset.source);
    }
    return noimage;
  }, [getDetail.data]);

  const balloonImage = useMemo(() => {
    if (getBalloon.data?.getSpotAsset) {
      if (getBalloon.data.getSpotAsset.source.length === 0) {
        return noimage;
      }
      return createObjectURL(getBalloon.data.getSpotAsset.source);
    }
    return noimage;
  }, [getDetail.data]);

  const handleInputFile = (
    e: React.ChangeEvent<HTMLInputElement>,
    assetType: SpotAssetType
  ) => {
    if (!e.target.files) {
      return;
    }
    const file = e.target.files[0];

    file.arrayBuffer().then((buffer) => {
      // ArrayBuffer → Uint8Array に変換
      const source = Array.from(new Uint8Array(buffer));

      update({
        spotId: spotId,
        assetType: assetType,
        source: source,
      }).then((res) => {
        if (res.error) {
          toast({
            title: "画像の変更に失敗しました:" + res.error.name,
            status: "error",
          });
          console.error("Upload failed", res);
          return;
        }
        toast({
          title: "画像を変更しました",
          status: "success",
        });
      });
    });
  };

  const fileUploadEvent = (assetType: SpotAssetType) => {
    switch (assetType) {
      case SpotAssetType.Detail:
        detail_ref.current?.click();
        break;
      case SpotAssetType.Thumb:
        thumb_ref.current?.click();
        break;
      case SpotAssetType.Balloon:
        balloon_ref.current?.click();
        break;
      default:
        return;
    }
  };

  const removeSpotAsset = (assetType: SpotAssetType) => {
    remove({
      spotId: spotId,
      assetType: assetType,
    }).then((res) => {
      if (res.error) {
        toast({
          title: "スポット画像の削除に失敗しました",
          status: "error",
        });
        console.error(res.error);
      } else {
        toast({
          title: "スポット画像を削除しました",
          status: "success",
        });
      }
    });
  };

  return (
    <HStack w="full" p={1} h="full">
      <VStack display="flex" flexDir="column" flex={1} h="full">
        <FormControl display="flex" flexDir="column" flex={1}>
          <HStack flexDir="row" flex={1} align={"start"}>
            <FormLabel color="white">詳細</FormLabel>
            <IconButton
              aria-label="upload asset"
              onClick={() => fileUploadEvent(SpotAssetType.Detail)}
              icon={<AttachmentIcon />}
              colorScheme="green"
            />
            <IconButton
              aria-label="remove asset"
              onClick={() => removeSpotAsset(SpotAssetType.Detail)}
              icon={<DeleteIcon />}
              isDisabled={detailImage === noimage}
              colorScheme="green"
            />
          </HStack>
          <Flex direction="column" height="full" justify="space-between">
            <input
              hidden
              ref={detail_ref}
              type="file"
              accept="image/*" // 画像ファイルのみ選択できるようにする
              onChange={(e) => {
                handleInputFile(e, SpotAssetType.Detail);
              }}
            />
            <Image
              src={detailImage}
              maxHeight={280}
              maxWidth={250}
              fit="contain"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = noimage;
              }}
            />

            <Button
              onClick={() => {
                setSpotIdForEdit(undefined);
              }}
              size="sm"
              rounded="full"
              color="white"
              colorScheme="green"
            >
              全体の設定
            </Button>
          </Flex>
        </FormControl>
      </VStack>

      <VStack display="flex" flexDir="column" flex={1} h="full">
        <FormControl display="flex" flexDir="column" flex={1}>
          <HStack flexDir="row" flex={1} align={"start"}>
            <FormLabel color="white">サムネ</FormLabel>
            <IconButton
              aria-label="upload asset"
              onClick={() => fileUploadEvent(SpotAssetType.Thumb)}
              icon={<AttachmentIcon />}
              colorScheme="green"
            />
            <IconButton
              aria-label="remove asset"
              onClick={() => removeSpotAsset(SpotAssetType.Thumb)}
              icon={<DeleteIcon />}
              isDisabled={thumbImage === noimage}
              colorScheme="green"
            />
          </HStack>
          <input
            hidden
            ref={thumb_ref}
            type="file"
            accept="image/*" // 画像ファイルのみ選択できるようにする
            onChange={(e) => handleInputFile(e, SpotAssetType.Thumb)}
          />
          <Flex direction="column" height="full" justify="space-between">
            <Image
              src={thumbImage}
              maxHeight={300}
              maxWidth={250}
              fit="contain"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = noimage;
              }}
            />
          </Flex>
        </FormControl>
      </VStack>

      <VStack display="flex" flexDir="column" flex={1} h="full">
        <FormControl display="flex" flexDir="column" flex={1}>
          <HStack flexDir="row" flex={1} align={"start"}>
            <FormLabel color="white">バルーン</FormLabel>
            <IconButton
              aria-label="upload asset"
              onClick={() => fileUploadEvent(SpotAssetType.Balloon)}
              icon={<AttachmentIcon />}
              colorScheme="green"
            />
            <IconButton
              aria-label="remove asset"
              onClick={() => removeSpotAsset(SpotAssetType.Balloon)}
              icon={<DeleteIcon />}
              isDisabled={balloonImage === noimage}
              colorScheme="green"
            />
          </HStack>
          <input
            hidden
            ref={balloon_ref}
            type="file"
            accept="image/*" // 画像ファイルのみ選択できるようにする
            onChange={(e) => handleInputFile(e, SpotAssetType.Balloon)}
          />
          <Flex direction="column" height="full" justify="space-between">
            <Image
              src={balloonImage}
              maxHeight={300}
              maxWidth={250}
              fit="contain"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = noimage;
              }}
            />
          </Flex>
        </FormControl>
      </VStack>
    </HStack>
  );
};

const CourseImageForm = ({ courseId }: { courseId: string }) => {
  const toast = useToast();
  const thumb_ref = useRef<HTMLInputElement>(null);
  const start_ref = useRef<HTMLInputElement>(null);
  const course_ref = useRef<HTMLInputElement>(null);
  const half_ref = useRef<HTMLInputElement>(null);
  const nomap_ref = useRef<HTMLInputElement>(null);
  const logo_ref = useRef<HTMLInputElement>(null);

  const [getThumb] = useGetCourseAssetQuery({
    variables: {
      courseId: courseId,
      assetType: CourseAssetType.Thumb,
    },
  });
  const [getStart] = useGetCourseAssetQuery({
    variables: {
      courseId: courseId,
      assetType: CourseAssetType.Start,
    },
  });
  const [getCourse] = useGetCourseAssetQuery({
    variables: {
      courseId: courseId,
      assetType: CourseAssetType.Course,
    },
  });
  const [getHalf] = useGetCourseAssetQuery({
    variables: {
      courseId: courseId,
      assetType: CourseAssetType.Half,
    },
  });
  const [getNomap] = useGetCourseAssetQuery({
    variables: {
      courseId: courseId,
      assetType: CourseAssetType.Nomap,
    },
  });
  const [getLogo] = useGetCourseAssetQuery({
    variables: {
      courseId: courseId,
      assetType: CourseAssetType.Logo,
    },
  });

  const [updateRes, update] = useUpdateCourseAssetMutation();
  const [removeCourseAssetRes, remove] = useRemoveCourseAssetMutation();
  const noimage = "";
  const thumbImage = useMemo(() => {
    if (getThumb.data?.getCourseAsset) {
      if (getThumb.data.getCourseAsset.source.length === 0) {
        return noimage;
      }
      return createObjectURL(getThumb.data.getCourseAsset.source);
    }
    return noimage;
  }, [getThumb.data]);

  const startImage = useMemo(() => {
    if (getStart.data?.getCourseAsset) {
      if (getStart.data.getCourseAsset.source.length === 0) {
        return noimage;
      }
      return createObjectURL(getStart.data.getCourseAsset.source);
    }
    return noimage;
  }, [getStart.data]);

  const courseImage = useMemo(() => {
    if (getCourse.data?.getCourseAsset) {
      if (getCourse.data.getCourseAsset.source.length === 0) {
        return noimage;
      }
      return createObjectURL(getCourse.data.getCourseAsset.source);
    }
    return noimage;
  }, [getCourse.data]);

  const halfImage = useMemo(() => {
    if (getHalf.data?.getCourseAsset) {
      if (getHalf.data.getCourseAsset.source.length === 0) {
        return noimage;
      }
      return createObjectURL(getHalf.data.getCourseAsset.source);
    }
    return noimage;
  }, [getHalf.data]);

  const nomapImage = useMemo(() => {
    if (getNomap.data?.getCourseAsset) {
      if (getNomap.data.getCourseAsset.source.length === 0) {
        return noimage;
      }
      return createObjectURL(getNomap.data.getCourseAsset.source);
    }
    return noimage;
  }, [getNomap.data]);

  const logoImage = useMemo(() => {
    if (getLogo.data?.getCourseAsset) {
      if (getLogo.data.getCourseAsset.source.length === 0) {
        return noimage;
      }
      return createObjectURL(getLogo.data.getCourseAsset.source);
    }
    return noimage;
  }, [getLogo.data]);

  const handleInputFile = (
    e: React.ChangeEvent<HTMLInputElement>,
    assetType: CourseAssetType
  ) => {
    if (!e.target.files) {
      return;
    }

    const file = e.target.files[0];
    file.arrayBuffer().then((buffer) => {
      // ArrayBuffer → Uint8Array に変換
      const source = Array.from(new Uint8Array(buffer));

      update({
        courseId: courseId,
        assetType: assetType,
        source: source,
      }).then((res) => {
        if (res.error) {
          toast({
            title: "画像の変更に失敗しました:" + res.error,
            status: "error",
          });
          console.error("Upload failed", res);
          return;
        }
        toast({
          title: "画像を変更しました",
          status: "success",
        });
      });
    });
  };

  const fileUploadEvent = (assetType: CourseAssetType) => {
    switch (assetType) {
      case CourseAssetType.Thumb:
        thumb_ref.current?.click();
        break;
      case CourseAssetType.Start:
        start_ref.current?.click();
        break;
      case CourseAssetType.Course:
        course_ref.current?.click();
        break;
      case CourseAssetType.Half:
        half_ref.current?.click();
        break;
      case CourseAssetType.Nomap:
        nomap_ref.current?.click();
        break;
      case CourseAssetType.Logo:
        logo_ref.current?.click();
        break;
      default:
        return;
    }
  };

  const removeCourseAsset = (assetType: CourseAssetType) => {
    remove({
      courseId: courseId,
      assetType: assetType,
    }).then((res) => {
      if (res.error) {
        toast({
          title: "コース画像の削除に失敗しました",
          status: "error",
        });
        console.error(res.error);
      } else {
        toast({
          title: "コース画像を削除しました",
          status: "success",
        });
      }
    });
  };

  return (
    <HStack w="full" p={1} h="full" align="start">
      <VStack
        display="flex"
        flexDir="column"
        flex="1 1 300px"
        minW="300px"
        maxW="100%"
        h="full"
      >
        <FormControl display="flex" flexDir="column" flex={1}>
          <HStack flexDir="row" flex={1} align={"start"}>
            <FormLabel color="white">コース(ルート付き)</FormLabel>
            <IconButton
              aria-label="upload asset"
              onClick={() => fileUploadEvent(CourseAssetType.Course)}
              icon={<AttachmentIcon />}
              colorScheme="green"
            />
            <IconButton
              aria-label="remove asset"
              onClick={() => removeCourseAsset(CourseAssetType.Course)}
              icon={<DeleteIcon />}
              isDisabled={courseImage === noimage}
              colorScheme="green"
            />
          </HStack>
          <Flex direction="column" height="full" justify="space-between">
            <Image
              src={courseImage}
              maxHeight={150}
              maxWidth={250}
              fit="contain"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = noimage;
              }}
            />
          </Flex>
          <HStack flexDir="row" flex={1} align={"start"}>
            <FormLabel color="white">コース(ルート無し)</FormLabel>
            <IconButton
              aria-label="upload asset"
              onClick={() => fileUploadEvent(CourseAssetType.Nomap)}
              icon={<AttachmentIcon />}
              colorScheme="green"
            />
            <IconButton
              aria-label="remove asset"
              onClick={() => removeCourseAsset(CourseAssetType.Nomap)}
              icon={<DeleteIcon />}
              isDisabled={nomapImage === noimage}
              colorScheme="green"
            />
          </HStack>
          <Flex direction="column" height="full" justify="space-between">
            <Image
              src={nomapImage}
              maxHeight={150}
              maxWidth={250}
              fit="contain"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = noimage;
              }}
            />
          </Flex>
          <input
            hidden
            ref={course_ref}
            type="file"
            accept="image/*" // 画像ファイルのみ選択できるようにする
            onChange={(e) => handleInputFile(e, CourseAssetType.Course)}
          />
          <input
            hidden
            ref={nomap_ref}
            type="file"
            accept="image/*" // 画像ファイルのみ選択できるようにする
            onChange={(e) => handleInputFile(e, CourseAssetType.Nomap)}
          />
        </FormControl>
      </VStack>

      <VStack
        display="flex"
        flexDir="column"
        flex="1 1 300px"
        minW="300px"
        maxW="100%"
        h="full"
      >
        <FormControl display="flex" flexDir="column" flex={1}>
          <HStack flexDir="row" flex={1} align={"start"}>
            <FormLabel color="white">コース(ハーフ)</FormLabel>
            <IconButton
              aria-label="upload asset"
              onClick={() => fileUploadEvent(CourseAssetType.Half)}
              icon={<AttachmentIcon />}
              colorScheme="green"
            />
            <IconButton
              aria-label="remove asset"
              onClick={() => removeCourseAsset(CourseAssetType.Half)}
              icon={<DeleteIcon />}
              isDisabled={halfImage === noimage}
              colorScheme="green"
            />
          </HStack>
          <Flex direction="column" height="full" justify="space-between">
            <Image
              src={halfImage}
              maxHeight={150}
              maxWidth={250}
              fit="contain"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = noimage;
              }}
            />
          </Flex>
          <HStack flexDir="row" flex={1} align={"start"}>
            <FormLabel color="white">スタート地点</FormLabel>
            <IconButton
              aria-label="upload asset"
              onClick={() => fileUploadEvent(CourseAssetType.Start)}
              icon={<AttachmentIcon />}
              colorScheme="green"
            />
            <IconButton
              aria-label="upload asset"
              onClick={() => removeCourseAsset(CourseAssetType.Start)}
              icon={<DeleteIcon />}
              isDisabled={startImage === noimage}
              colorScheme="green"
            />
          </HStack>
          <Flex direction="column" height="full" justify="space-between">
            <Image
              src={startImage}
              maxHeight={150}
              maxWidth={250}
              fit="contain"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = noimage;
              }}
            />
          </Flex>
          <input
            hidden
            ref={half_ref}
            type="file"
            accept="image/png" // 画像ファイルのみ選択できるようにする
            onChange={(e) => handleInputFile(e, CourseAssetType.Half)}
          />
          <input
            hidden
            ref={start_ref}
            type="file"
            accept="image/png" // 画像ファイルのみ選択できるようにする
            onChange={(e) => handleInputFile(e, CourseAssetType.Start)}
          />
        </FormControl>
      </VStack>

      <VStack
        display="flex"
        flexDir="column"
        flex="1 1 300px"
        minW="300px"
        maxW="100%"
        h="full"
      >
        <FormControl display="flex" flexDir="column" flex={1}>
          <HStack flexDir="row" flex={1} align={"start"}>
            <FormLabel color="white">サムネ</FormLabel>
            <IconButton
              aria-label="upload asset"
              onClick={() => fileUploadEvent(CourseAssetType.Thumb)}
              icon={<AttachmentIcon />}
              colorScheme="green"
            />
            <IconButton
              aria-label="remove asset"
              onClick={() => removeCourseAsset(CourseAssetType.Thumb)}
              icon={<DeleteIcon />}
              isDisabled={thumbImage === noimage}
              colorScheme="green"
            />
          </HStack>
          <Flex direction="column" height="full" justify="space-between">
            <Image
              src={thumbImage}
              maxHeight={150}
              maxWidth={250}
              fit="contain"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = noimage;
              }}
            />
          </Flex>
          <HStack flexDir="row" flex={1} align={"start"}>
            <FormLabel color="white">コラボロゴ</FormLabel>
            <IconButton
              aria-label="upload asset"
              onClick={() => fileUploadEvent(CourseAssetType.Logo)}
              icon={<AttachmentIcon />}
              colorScheme="green"
            />
            <IconButton
              aria-label="remove asset"
              onClick={() => removeCourseAsset(CourseAssetType.Logo)}
              icon={<DeleteIcon />}
              isDisabled={logoImage === noimage}
              colorScheme="green"
            />
          </HStack>
          <Flex direction="column" height="full" justify="space-between">
            <Image
              src={logoImage}
              maxHeight={150}
              maxWidth={250}
              fit="contain"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = noimage;
              }}
            />
          </Flex>
          <input
            hidden
            ref={thumb_ref}
            type="file"
            accept="image/png" // 画像ファイルのみ選択できるようにする
            onChange={(e) => handleInputFile(e, CourseAssetType.Thumb)}
          />
          <input
            hidden
            ref={logo_ref}
            type="file"
            accept="image/png" // 画像ファイルのみ選択できるようにする
            onChange={(e) => handleInputFile(e, CourseAssetType.Logo)}
          />
        </FormControl>
      </VStack>
    </HStack>
  );
};

export const ImageEditor = ({ courseId }: { courseId: string }) => {
  return (
    <Flex
      bottom={0}
      position="fixed"
      bgColor="green.900"
      w="calc(100vw - 500px)"
      h={400}
      flexDir="row"
    >
      <Suspense
        fallback={
          <Box w="full" h="full" m={16}>
            <Spinner />
          </Box>
        }
      >
        <Inner courseId={courseId} />
      </Suspense>
    </Flex>
  );
};

function createObjectURL(source: number[]) {
  const bytes = new Uint8Array(source);

  let blob = new Blob([bytes], { type: "image/png" });
  return URL.createObjectURL(blob);
}
